import React from "react";
import {
  User,
  Calendar,
  Phone,
  Mail,
  MapPin,
  Clock,
  Stethoscope,
  Heart,
  Pill,
  FileText,
  CreditCard,
  ClipboardList,
  Activity,
  CheckCircle,
  Clock3,
  AlertCircle,
  Edit,
} from "lucide-react";

const detailsStyles = `
  .details-container {
    padding: 20px;
    background: #f8f9fa;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }

  .details-card {
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 24px;
    margin-bottom: 16px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  }

  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    padding-bottom: 12px;
    border-bottom: 1px solid #e9ecef;
  }

  .section-title-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .section-icon {
    color: #6c757d;
  }

  .section-title {
    font-size: 16px;
    font-weight: 600;
    color: #212529;
    margin: 0;
  }

  .edit-button {
    display: flex;
    align-items: center;
    gap: 4px;
    background: none;
    border: none;
    color: #007bff;
    font-size: 14px;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    transition: background-color 0.2s;
  }

  .edit-button:hover {
    background: #f8f9fa;
  }

  .detail-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  .detail-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .detail-item.full-width {
    grid-column: 1 / -1;
  }

  .detail-label {
    font-size: 13px;
    font-weight: 500;
    color: #6c757d;
    margin: 0;
  }

  .detail-value {
    font-size: 14px;
    color: #212529;
    font-weight: 400;
    margin: 0;
  }

  .list-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .list-item {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 12px;
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 6px;
  }

  .list-item-icon {
    color: #6c757d;
    margin-top: 2px;
    flex-shrink: 0;
  }

  .list-item-text {
    font-size: 14px;
    color: #212529;
    line-height: 1.4;
  }

  .diagnosis-box, .treatment-box, .discharge-box {
    padding: 16px;
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    font-size: 14px;
    color: #212529;
    line-height: 1.5;
  }

  .reports-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 16px;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    overflow: hidden;
  }

  .reports-table th {
    background: #f8f9fa;
    color: #495057;
    padding: 12px;
    text-align: left;
    font-weight: 600;
    font-size: 13px;
    border-bottom: 1px solid #e9ecef;
  }

  .reports-table td {
    padding: 12px;
    border-bottom: 1px solid #f1f3f4;
    font-size: 14px;
    color: #212529;
  }

  .reports-table tr:last-child td {
    border-bottom: none;
  }

  .reports-table tr:hover {
    background: #f8f9fa;
  }

  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
  }

  .status-reviewed {
    background: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  }

  .status-pending {
    background: #fff3cd;
    color: #856404;
    border: 1px solid #ffeaa7;
  }

  .billing-total {
    background: #e7f3ff;
    border: 1px solid #b3d9ff;
    border-radius: 6px;
    padding: 16px;
    margin-top: 12px;
  }

  .billing-total .detail-value {
    font-size: 18px;
    font-weight: 600;
    color: #0056b3;
  }

  @media (max-width: 768px) {
    .detail-grid {
      grid-template-columns: 1fr;
    }
    
    .section-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    }
  }
`;

const OutPatientDetails = ({ patient }) => {
  const StatusBadge = ({ status }) => {
    const isReviewed = status === "Reviewed";
    return (
      <span
        className={`status-badge ${
          isReviewed ? "status-reviewed" : "status-pending"
        }`}
      >
        {isReviewed ? <CheckCircle size={12} /> : <Clock3 size={12} />}
        {status}
      </span>
    );
  };

  return (
    <>
      <style>{detailsStyles}</style>
      <div className="details-container">
        {/* Patient Details */}
        <div className="details-card" id="patient-details">
          <div className="section-header">
            <div className="section-title-wrapper">
              <User className="section-icon" size={20} />
              <h3 className="section-title">Basic Information</h3>
            </div>
            {/* <button className="edit-button">
              <Edit size={14} />
              Edit Info
            </button> */}
          </div>
          <div className="detail-grid">
            <div className="detail-item">
              <div className="detail-label">Patient ID</div>
              <div className="detail-value">
                {patient?.patientInfo?.patientID ?? "N/A"}
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-label">Full Name</div>
              <div className="detail-value">
                {patient?.patientInfo?.patientName ?? "N/A"}
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-label">Age</div>
              <div className="detail-value">
                {patient?.patientInfo?.age ?? "N/A"} Years
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-label">Gender</div>
              <div className="detail-value">
                {patient?.patientInfo?.gender ?? "N/A"}
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-label">Phone</div>
              <div className="detail-value">
                {patient?.patientInfo?.contactNumber ?? "N/A"}
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-label">Email</div>
              <div className="detail-value">
                {patient?.patientInfo?.emailAddress ?? "N/A"}
              </div>
            </div>
            <div className="detail-item full-width">
              <div className="detail-label">Address</div>
              <div className="detail-value">
                {patient?.patientInfo?.address ?? "N/A"}
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-label">Registration Date</div>
              <div className="detail-value">
                {patient?.patientInfo?.registeredOn
                  ? new Date(patient.patientInfo.registeredOn)
                      .toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                      .replace(/ /g, "-")
                  : "N/A"}
              </div>
            </div>
          </div>
        </div>

        {/* Appointment Info */}
        <div className="details-card" id="appointment-info">
          <div className="section-header">
            <div className="section-title-wrapper">
              <Calendar className="section-icon" size={20} />
              <h3 className="section-title">Appointment Details</h3>
            </div>
            {/* <button className="edit-button">
              <Edit size={14} />
              Edit Info
            </button> */}
          </div>
          <div className="detail-grid">
            <div className="detail-item">
              <div className="detail-label">Doctor</div>
              <div className="detail-value">
                {patient?.patientInfo?.doctorName ?? "N/A"}
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-label">Department</div>
              <div className="detail-value">
                {patient?.patientInfo?.department ?? "N/A"}
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-label">Appointment Date</div>
              <div className="detail-value">
                {patient?.patientInfo?.appointmentDate
                  ? new Date(patient.patientInfo.appointmentDate)
                      .toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                      .replace(/ /g, "-")
                  : "N/A"}
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-label">Time</div>
              <div className="detail-value">
                {patient?.patientInfo?.time ?? "N/A"}
              </div>
            </div>
            <div className="detail-item full-width">
              <div className="detail-label">Visit Reason</div>
              <div className="detail-value">
                {patient?.patientInfo?.appointmentType ?? "N/A"}
              </div>
            </div>
          </div>
        </div>

        {/* Medical History */}
        <div className="details-card">
          <div className="section-header">
            <div className="section-title-wrapper">
              <Activity className="section-icon" size={20} />
              <h3 className="section-title">Medical History</h3>
            </div>
            {/* <button className="edit-button">
              <Edit size={14} />
              Edit Info
            </button> */}
          </div>
          <div className="list-container">
            <div className="list-item">
              <AlertCircle className="list-item-icon" size={16} />
              <span className="list-item-text">
                {patient?.patientInfo?.existingMedicalConditions ?? "N/A"}
              </span>
            </div>
          </div>
        </div>

        {/* Diagnosis */}
        <div className="details-card">
          <div className="section-header">
            <div className="section-title-wrapper">
              <Stethoscope className="section-icon" size={20} />
              <h3 className="section-title">Current Diagnosis</h3>
            </div>
            {/* <button className="edit-button">
              <Edit size={14} />
              Edit Info
            </button> */}
          </div>
          <div className="diagnosis-box">
            {patient?.patientInfo?.diagnosis ?? "N/A"}
          </div>
        </div>

        {/* Prescriptions */}
        <div className="details-card" id="prescriptions">
          <div className="section-header">
            <div className="section-title-wrapper">
              <Pill className="section-icon" size={20} />
              <h3 className="section-title">Current Prescriptions</h3>
            </div>
            {/* <button className="edit-button">
              <Edit size={14} />
              Edit Info
            </button> */}
          </div>
          <div className="list-container">
            {patient?.prescriptions?.map((item, idx) => (
              <div key={idx} className="list-item">
                <Pill className="list-item-icon" size={16} />
                <span className="list-item-text">
                  {item.medicationName +
                    " " +
                    item?.dosage +
                    "-" +
                    item?.frequency}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Reports */}
        <div className="details-card" id="reports">
          <div className="section-header">
            <div className="section-title-wrapper">
              <FileText className="section-icon" size={20} />
              <h3 className="section-title">Lab Reports & Tests</h3>
            </div>
            {/* <button className="edit-button">
              <Edit size={14} />
              Edit Info
            </button> */}
          </div>
          <table className="reports-table">
            <thead>
              <tr>
                <th>Report Name</th>
                <th>Report Type</th>
                <th>Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {patient?.labReports?.map((report, idx) => (
                <tr key={idx}>
                  <td style={{ fontWeight: "500" }}>{report.testName}</td>
                  <td>{report.reportType}</td>
                  <td>
                    {new Date(report.reportDate)
                      .toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                      .replace(/ /g, "-")}
                  </td>
                  <td>
                    <StatusBadge status={report.labReportStatus} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Treatment Plan */}
        <div className="details-card">
          <div className="section-header">
            <div className="section-title-wrapper">
              <Heart className="section-icon" size={20} />
              <h3 className="section-title">Treatment Plan</h3>
            </div>
            {/* <button className="edit-button">
              <Edit size={14} />
              Edit Info
            </button> */}
          </div>
          <div className="treatment-box">
            {patient?.patientInfo?.treatmentPlan_Summary ?? "N/A"}
          </div>
        </div>

        {/* Billing Summary */}
        <div className="details-card" id="billing-summary">
          <div className="section-header">
            <div className="section-title-wrapper">
              <CreditCard className="section-icon" size={20} />
              <h3 className="section-title">Billing Summary</h3>
            </div>
            {/* <button className="edit-button">
              <Edit size={14} />
              Edit Info
            </button> */}
          </div>
          {/* <div className="detail-grid">
            <div className="detail-item">
              <div className="detail-label">Consultation Fee</div>
              <div className="detail-value">{patient?.billing?.consultation}</div>
            </div>
            <div className="detail-item">
              <div className="detail-label">Tests & Reports</div>
              <div className="detail-value">{patient?.billing?.tests}</div>
            </div>
            <div className="detail-item">
              <div className="detail-label">Payment Status</div>
              <div className="detail-value">
                <span className="status-badge status-reviewed">
                  <CheckCircle size={12} />
                  {patient?.billing?.status}
                </span>
              </div>
            </div>
          </div> */}
          <table className="reports-table">
            <thead>
              <tr>
                <th>Tests & Reports</th>
                <th>Consultation Fee (&#x20b9;)</th>
              </tr>
            </thead>
            <tbody>
              {/* Map test items */}
              {patient?.testItems?.map((report, idx) => (
                <tr key={idx}>
                  <td style={{ fontWeight: "500" }}>{report.testName}</td>
                  <td>{report.itemAmount}</td>
                </tr>
              ))}

              {/* Fixed consultation fee row */}
              {patient?.patientInfo?.appointmentFee && (
                <tr>
                  <td style={{ fontWeight: "600" }}>Consultation Fee</td>
                  <td style={{ fontWeight: "500" }}>
                    {patient?.patientInfo?.appointmentFee || "NA"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="billing-total">
            <div className="detail-item">
              <div className="detail-label">Total Amount</div>
              <div className="detail-value">
                &#x20b9;
                {patient?.patientInfo?.totalBill || "0"}
              </div>
            </div>
          </div>
        </div>

        {/* Discharge Summary */}
        <div className="details-card" id="discharge-summary">
          <div className="section-header">
            <div className="section-title-wrapper">
              <ClipboardList className="section-icon" size={20} />
              <h3 className="section-title">Discharge Summary</h3>
            </div>
            {/* <button className="edit-button">
              <Edit size={14} />
              Edit Info
            </button> */}
          </div>
          <div className="discharge-box">
            {patient?.patientInfo?.dischargeSummary ?? "N/A"}
          </div>
        </div>
      </div>
    </>
  );
};

// Demo data for preview
const demoPatient = {
  patientId: "PAT-2024-001",
  name: "John Doe",
  age: 45,
  gender: "Male",
  contact: "+1 (555) 123-4567",
  email: "john.doe@email.com",
  address: "123 Main Street, Springfield, IL 62701",
  registrationDate: "2024-01-15",
  doctor: "Dr. Sarah Wilson",
  department: "Cardiology",
  appointmentDate: "2024-07-23",
  appointmentTime: "10:30 AM",
  visitReason: "Routine cardiac checkup and chest pain evaluation",
  medicalHistory: [
    "Hypertension diagnosed in 2020",
    "Type 2 Diabetes mellitus since 2018",
    "Previous myocardial infarction in 2021",
  ],
  diagnosis:
    "Stable angina pectoris with well-controlled diabetes and hypertension. Patient shows good response to current medication regimen.",
  prescriptions: [
    "Metformin 500mg - Twice daily with meals",
    "Lisinopril 10mg - Once daily in the morning",
    "Atorvastatin 20mg - Once daily at bedtime",
    "Aspirin 81mg - Once daily with breakfast",
  ],
  reports: [
    { name: "Complete Blood Count", date: "2024-07-20", status: "Reviewed" },
    { name: "Lipid Profile", date: "2024-07-20", status: "Reviewed" },
    { name: "ECG", date: "2024-07-23", status: "Pending" },
    { name: "Chest X-Ray", date: "2024-07-23", status: "Pending" },
  ],
  treatmentPlan:
    "Continue current medication regimen. Follow up in 3 months for routine monitoring. Patient advised to maintain low-sodium diet and regular exercise as tolerated. Monitor blood glucose levels daily and maintain diabetes management plan.",
  billing: {
    consultation: "$150.00",
    tests: "$275.00",
    total: "$425.00",
    status: "Paid",
  },
  dischargeSummary:
    "Patient presented for routine cardiac follow-up. Vital signs stable, no acute distress. Current medications well-tolerated with good therapeutic response. Patient counseled on lifestyle modifications and medication compliance. Next appointment scheduled in 3 months.",
};

export default ({ patient }) => <OutPatientDetails patient={patient} />;
